import { useState, useMemo, useEffect } from "react";
import { formatPercent } from "../../utils/formatNumber";
import "./Location.css";

export default function LocationTables({
  countries = [],
  cities = [],
  loading = false,
  selectedCountry = null,
}) {
  // Filter cities by selected country if a country is selected
  const filteredCities = useMemo(() => {
    if (!selectedCountry) return cities;
    return cities.filter((city) => city.country === selectedCountry);
  }, [cities, selectedCountry]);

  return (
    <div className="location-grid">
      <LocationTable
        title="Country Breakdown"
        rows={countries}
        loading={loading}
      />
      <LocationTable
        title="City Breakdown"
        rows={filteredCities}
        loading={loading}
        selectedCountry={selectedCountry}
      />
    </div>
  );
}
const displayCountry = (name) => {
  if (name === "Turkey") return "Türkiye";
  return name;
};
function LocationTable({ title, rows, loading, selectedCountry = null }) {
  const [visibleCount, setVisibleCount] = useState(20);
  const INITIAL_COUNT = 20;
  const INCREMENT = 20;

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => b.value - a.value);
  }, [rows]);

  const max = sorted[0]?.value || 1;
  const visibleRows = sorted.slice(0, visibleCount);
  const hasMore = sorted.length > visibleCount;

  // Reset visible count when rows change (e.g., country selection)
  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
  }, [rows.length, selectedCountry]);

  const handleShowMore = () => {
    setVisibleCount((prev) => Math.min(prev + INCREMENT, sorted.length));
  };

  return (
    <div className="location-box">
      <div className="location-box-header">
        <h4>
          {title}
          {selectedCountry && title === "City Breakdown" && (
            <span style={{ 
              fontSize: "12px", 
              fontWeight: "400", 
              color: "var(--text-secondary)",
              marginLeft: "8px"
            }}>
              ({displayCountry(selectedCountry)})
            </span>
          )}
        </h4>
      </div>

      {/* ✅ LOADING */}
      {loading && (
        <div className="location-table-skeleton">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="location-skel-row">
              <div className="location-skel-rank" />
              <div className="location-skel-name" />
              <div className="location-skel-bar" />
            </div>
          ))}
        </div>
      )}

      {/* ✅ REAL EMPTY */}
      {!loading && rows.length === 0 && (
        <div className="location-empty">No data</div>
      )}

      {/* ✅ DATA */}
      {!loading && rows.length > 0 && (
        <>
          <table className="location-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th style={{ width: "60%" }}>Rate</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr key={r.key + i}>
                  <td className="muted">{i + 1}</td>
                  <td className="strong">{displayCountry(r.key)}</td>
                  <td>
                    <div className="rate-cell">
                      <div className="rate-bar">
                        <span
                          style={{
                            width: `${(r.value / max) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="rate-value">
                        {formatPercent(r.value, 1)}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {hasMore && (
            <div style={{ 
              marginTop: "16px", 
              textAlign: "center",
              paddingTop: "12px",
              borderTop: "1px solid var(--border-color)"
            }}>
              <button
                onClick={handleShowMore}
                style={{
                  padding: "8px 24px",
                  backgroundColor: "var(--bg-secondary)",
                  color: "var(--text-primary)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "600",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = "var(--chart-primary)";
                  e.target.style.color = "#ffffff";
                  e.target.style.borderColor = "var(--chart-primary)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "var(--bg-secondary)";
                  e.target.style.color = "var(--text-primary)";
                  e.target.style.borderColor = "var(--border-color)";
                }}
              >
                Show More ({sorted.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
